
import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { eIdentityVerificationResult, eIdentityVerificationSource, eNormalizedEntityType, eNormalizedStatus, eOrderType, ePartyType, eResultState } from '../../enums';
import { Order } from '../../interfaces/orders/order';
import { OrderManagerService } from '../../services/order-manager/order-manager.service';
import { SearchResultsRepositoryService } from '../../services/search-results/search-results-repository.service';
import { BehaviorSubject, concatMap, of, Subject, takeUntil, tap } from 'rxjs';
import { Utilities } from '../../utilities';
import { Party } from '../../interfaces/parties/party';
import { OrderUIConfiguration } from '../../interfaces/ui-configurations/order-ui-configuration';
import { Address } from '../../interfaces/address/address';
import { IdentityVerificationDetailsService } from '../../services/identity-verification-details/identity-verification-details.service';
import { IdentityVerificationDetails } from '../../interfaces/identity-verification-details/identity-verification-details';
import { TranslocoService } from '@jsverse/transloco';
import { EnumLabelService } from '../../services/enum-label/enum-label.service';

@Component({
  selector: 'lib-ubo-order-details-tab-group',
  templateUrl: './ubo-order-details-tab-group.component.html',
  styleUrl: './ubo-order-details-tab-group.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UboOrderDetailsTabGroupComponent implements OnInit, OnDestroy {
  private readonly enumLabels = inject(EnumLabelService);
  private readonly transloco = inject(TranslocoService);
  @Input() public order!: Order;
  @Input() uiConfiguration?: OrderUIConfiguration;
  @Input() admin: boolean = false;
  protected results: unknown | null = null;
  protected shouldShowMaterialChangesAlert = false;
  protected attestor: Party | undefined;

  signingAuthorityPartyType: ePartyType = ePartyType.individualSigningAuthority;
  parties$ = new BehaviorSubject<Party[]>([]);
  private onDestroy$ = new Subject<void>();
  orderIdvDetails?: IdentityVerificationDetails;

  constructor(
    private _orderManager: OrderManagerService,
    private _searchResultsService: SearchResultsRepositoryService,
    private identityVerificationDetailsService: IdentityVerificationDetailsService) { }

  ngOnInit(): void {
    if (!this.order) {
      this._orderManager.currentOrder.pipe(
        takeUntil(this.onDestroy$),
        tap(order => {
          this.order = order;
          this.parties$.next(this.getSigningAuthorityParties());
          this.attestor = this.order.parties?.find(p => Utilities.isAttestor(p));

          if (this.order.orderTypeID === eOrderType.ultimateBeneficialOwnershipReport && this.order.identityVerificationDetails) {
            this.identityVerificationDetailsService.getIdentityVerificationDetails(this.order.id)
              .subscribe(idvDetails => {
                if (idvDetails) {
                  this.orderIdvDetails = idvDetails;
                }
              });
          }
        }),
        concatMap(order => {
          if (order.searchResult?.hasParsedResult &&
            order.searchResult?.resultState?.id !== eResultState.noResult) {
            return this._searchResultsService.getBusinessReportParsedResultsAsJson(order.id);
          } else {
            return of(null);
          }
        })
      ).subscribe(payload => this.results = payload);
    } else {
      this.attestor = this.order.parties?.find(p => Utilities.isAttestor(p));
      this.parties$.next(this.getSigningAuthorityParties());

      if (this.order.searchResult?.hasParsedResult) {
        this._searchResultsService.getBusinessReportParsedResultsAsJson(this.order.id).subscribe(results => this.results = results);
      }
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  // ---------- JSON helpers (unchanged) ----------
  private asRecord(v: unknown): Record<string, unknown> | undefined {
    return (v !== null && typeof v === 'object') ? (v as Record<string, unknown>) : undefined;
  }
  protected get<T = unknown>(path: string): T | undefined {
    const rec = this.asRecord(this.results);
    return rec?.[path] as T;
  }
  protected getFrom<T = unknown>(obj: unknown, key: string): T | string {
    const rec = this.asRecord(obj);
    return rec?.[key] as T ?? "";
  }
  protected getArray<T = unknown>(path: string): T[] {
    const val = this.get(path);
    return Array.isArray(val) ? (val as T[]) : [];
  }
  protected getString(path: string): string | undefined {
    const v = this.get(path);
    return typeof v === 'string' ? v : undefined;
  }
  protected getEnumString(path: string): string | undefined {
    const v = this.get(path);
    return typeof v === 'string' ? v : undefined;
  }
  protected spaceCase(val: string | undefined): string {
    return Utilities.spacePascalCase(val ?? "");
  }

  // ---------- Display helpers (unchanged from your version) ----------
  protected getStatusIcon(): string { /* same as yours */
    const status = this.getEnumString('normalizedStatus') as eNormalizedStatus | undefined;
    switch (status) {
      case eNormalizedStatus.active: return 'check_circle_outline';
      case eNormalizedStatus.inactive: return 'block';
      default: return '';
    }
  }
  protected getStatusIconColor(): { color: string; } {
    const status = this.getEnumString('normalizedStatus') as eNormalizedStatus | undefined;
    switch (status) {
      case eNormalizedStatus.active: return { color: '#5C7F46' };
      case eNormalizedStatus.inactive: return { color: '#FB2C38' };
      default: return { color: 'black' };
    }
  }
  protected getTypeIcon(): string {
    const type = this.getEnumString('normalizedEntityType') as eNormalizedEntityType | undefined;
    switch (type) {
      case eNormalizedEntityType.businessName: return 'domain';
      case eNormalizedEntityType.corporation: return 'finance';
      default: return '';
    }
  }
  protected addressTypeLabel(address: unknown): string {
    const type = this.getFrom<string>(address, 'normalizedAddressType');
    if (!type || type === 'none') return this.transloco.translate('common.labels.address');
    return this.enumLabels.label('normalizedAddressType', type);
  }
  protected partyTypeLabel(party: unknown): string {
    const type = this.getFrom<string>(party, 'normalizedPartyType');
    if (!type || type === 'none') return this.transloco.translate('common.labels.none');
    return this.enumLabels.label('normalizedPartyType', type);
  }
  protected getCivicAddress(party: unknown): string | undefined {
    const addresses = this.getFrom(party, 'addresses');
    if (Array.isArray(addresses) && addresses.length > 0) {
      const first = addresses[0];
      return typeof this.getFrom(first, 'fullAddress') === 'string'
        ? (this.getFrom(first, 'fullAddress') as string)
        : '';
    }
    return '';
  }

  protected shouldShowBeneficialOwnersAlert() {
    return this.order.ultimateBeneficialOwnershipRequest?.shareholders.some(s => s.party.partyTypeID === ePartyType.businessBeneficialOwner);
  }

  protected getSigningAuthorityParties(): Party[] {
    let filteredParties = this.order.parties?.filter((party) => Utilities.isSigningAuthority(party));
    return filteredParties ?? [];
  }

  // ---------- Attestor helpers ----------
  protected hasAttestorVerification(): boolean {
    return (this.order.identityVerificationDetails?.sources ?? []).length > 0;
  }

  protected getReadableIDVMethod(): string {
    const idvSources = this.order.identityVerificationDetails?.sources ?? [];
    let stringSources: string[] = [];
    if (idvSources.some(s => s.name == eIdentityVerificationSource.EfxAmlSingleSource)) {
      stringSources.push(Utilities.getIdRequirementsString(eIdentityVerificationSource.EfxAmlSingleSource));
    }
    if (idvSources.some(s => s.name == eIdentityVerificationSource.TuCreditBureau)) {
      stringSources.push(Utilities.getIdRequirementsString(eIdentityVerificationSource.TuCreditBureau));
    }
    if (idvSources.some(s => s.name == eIdentityVerificationSource.EfxDvs)) {
      stringSources.push(Utilities.getIdRequirementsString(eIdentityVerificationSource.EfxDvs));
    }
    if (idvSources.some(s => s.name == eIdentityVerificationSource.EntrustDvs)) {
      stringSources.push(Utilities.getIdRequirementsString(eIdentityVerificationSource.EntrustDvs));
    }
    return stringSources.join(', ');
  }

  protected showIdVStatus(): boolean {
    if (this.orderIdvDetails?.sources?.length ?? 0 > 0) {
      return true;
    }
    return false;
  }

  protected showIdvResults(): boolean {
    return this.orderIdvDetails?.sources?.some(source => source.status === eIdentityVerificationResult.Fail || source.status === eIdentityVerificationResult.Review) ?? false;
  }

  protected getReadableIDVStatus(): string {
    if (this.orderIdvDetails?.sources?.some(s => s.status === eIdentityVerificationResult.Pending)) {
      return this.transloco.translate('lienLifetime.pending');
    } else if (this.orderIdvDetails?.sources?.some(s => s.status === eIdentityVerificationResult.Review)) {
      return this.transloco.translate('uboOrderDetailsTabGroup.idvStatus.review');
    } else if (this.orderIdvDetails?.sources?.some(s => s.status === eIdentityVerificationResult.Blocked)) {
      return this.transloco.translate('uboOrderDetailsTabGroup.idvStatus.blocked');
    } else if (this.orderIdvDetails?.sources?.some(s => s.status === eIdentityVerificationResult.Expired)) {
      return this.transloco.translate('lienLifetime.expired');
    } else if (this.orderIdvDetails?.sources?.some(s => s.status === eIdentityVerificationResult.Success)) {
      return this.transloco.translate('uboOrderDetailsTabGroup.idvStatus.passed');
    } else {
      return this.transloco.translate('uboOrderDetailsTabGroup.idvStatus.failed');
    }
  }

  protected formatAttestorAddress(address: Address | undefined): string {
    if (!address) return '';
    return [address.unit, address.streetNumber, address.streetName, address.city, address.jurisdiction, address.countryCode, address.postalCode]
      .filter(v => !!v)
      .join(' ');
  }
}